import React, { useState, useEffect } from 'react';
import FormTitle from '../../../../components/FormTitle';
import SecureImage from '../../../../components/BucketImage/Image';
import { downArrow } from '../../../../common/svg';
import { RouteData, RoutePoint, RouteSelectionProps, RouteWithUI } from '../../../../service/interfaces/props.interface';

interface ExtendedRouteSelectionProps extends RouteSelectionProps {
  setValue?: any;
  watch?: any;
  register?: any;
}

const RouteSelection: React.FC<ExtendedRouteSelectionProps> = ({
  routes = [],
  onRouteSelect,
  setValue,
  watch,
  register
}) => {
  // Get selected routes and expanded states from form state
  const selectedRoutes = watch ? (watch("selectedRoutes") || []) : [];
  const expandedRoutes = watch ? (watch("expandedRoutes") || {}) : {};

  // Local fallback state for when form functions are not provided
  const [localSelectedRoutes, setLocalSelectedRoutes] = useState<string[]>([]);
  const [localExpandedRoutes, setLocalExpandedRoutes] = useState<{ [key: string]: boolean }>({});

  // Use form state if available, otherwise use local state
  const currentSelectedRoutes = setValue && watch ? selectedRoutes : localSelectedRoutes;
  const currentExpandedRoutes = setValue && watch ? expandedRoutes : localExpandedRoutes;

  // Initialize expanded routes (first route expanded by default)
  useEffect(() => {
    if (routes.length > 0) {
      const initialExpanded = { [routes[0].routeId]: true };

      if (setValue && watch) {
        // Initialize form state if not already set
        if (!watch("expandedRoutes")) {
          setValue("expandedRoutes", initialExpanded);
        }
        if (!watch("selectedRoutes")) {
          setValue("selectedRoutes", []);
        }
      } else {
        // Initialize local state
        setLocalExpandedRoutes(initialExpanded);
      }
    }
  }, [routes, setValue, watch]);

  // Transform routes to include UI state
  const routesWithUI: RouteWithUI[] = routes.map((route) => ({
    ...route,
    isExpanded: currentExpandedRoutes[route.routeId] || false
  }));

  // Toggle route expansion
  const toggleRouteExpansion = (routeId: string) => {
    const newExpandedState = {
      ...currentExpandedRoutes,
      [routeId]: !currentExpandedRoutes[routeId]
    };

    if (setValue) {
      setValue("expandedRoutes", newExpandedState);
    } else {
      setLocalExpandedRoutes(newExpandedState);
    }
  };

  // Handle route selection (modified for multi-select)
  const handleRouteSelection = (routeId: string) => {
    const newSelection = currentSelectedRoutes.includes(routeId)
      ? currentSelectedRoutes.filter((id: string) => id !== routeId)
      : [...currentSelectedRoutes, routeId];

    if (setValue) {
      setValue("selectedRoutes", newSelection);
    } else {
      setLocalSelectedRoutes(newSelection);
    }

    if (onRouteSelect) {
      // Pass the array of selected routes, or null if empty
      onRouteSelect(newSelection.length > 0 ? newSelection.join(',') : null);
    }
  };

  // Format date to a readable string
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });
    } catch (error) {
      return dateString;
    }
  };

  // Get route points for a given route
  const getRoutePoints = (route: RouteData): RoutePoint[] => {
    const points: RoutePoint[] = [];

    if (route.pickupPoint) {
      points.push({
        name: 'Pickup Point',
        location: route.pickupPoint,
        time: formatDate(route.pickupAt)
      });
    }

    if (route.loadingPoint) {
      points.push({
        name: 'Loading Point',
        location: route.loadingPoint,
        time: formatDate(route.loadingAt)
      });
    }

    if (route.clearancePoint) {
      points.push({
        name: 'Clearance Point',
        location: route.clearancePoint,
        time: formatDate(route.clearanceAt)
      });
    }

    if (route.unloadingPoint) {
      points.push({
        name: 'Unloading Point',
        location: route.unloadingPoint,
        time: formatDate(route.unloadingAt)
      });
    }

    return points;
  };

  // Generate route title
  const getRouteTitle = (route: RouteData, index: number): string => {
    const routeNumber = `Route ${index + 1}`;
    const pickup = route.pickupPoint?.split(',')[0];
    const destination = route.unloadingPoint ? route.unloadingPoint?.split(',')[0] : route.clearancePoint?.split(',')[0];

    return `${routeNumber} - ${pickup} to ${destination}`;
  };

  return (
    <div className="w-full mx-auto p-4 bg-white rounded-lg shadow-sm">
      <div className="mb-6">
        <FormTitle title="Route Details" />
        <p className="text-[14px] font-medium mb-4">Select Route</p>
      </div>

      <div className="space-y-4">
        {routesWithUI?.map((route, index) => (
          <div
            key={route.routeId}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <div
              className="flex items-center p-4 cursor-pointer"
              onClick={() => toggleRouteExpansion(route.routeId)}
            >
              <input
                type="checkbox"
                name='route'
                id={`route-${route.routeId}`}
                checked={currentSelectedRoutes.includes(route.routeId)}
                onChange={() => handleRouteSelection(route.routeId)}
                className="mr-4 h-4 w-4 rounded border-gray-300 text-[#444FBD] focus:ring-[#444FBD]"
                onClick={(e) => e.stopPropagation()} // Prevent expansion toggle when clicking checkbox
              />
              <label
                htmlFor={`route-${route.routeId}`}
                className="flex-grow text-sm font-medium text-gray-900 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent expansion toggle when clicking label
                  handleRouteSelection(route.routeId);
                }}
              >
                <span>{getRouteTitle(route, index)}</span>
              </label>
              <SecureImage
                fileKey={downArrow}
                alt={route.isExpanded ? "Collapse" : "Expand"}
                className={`transition-transform duration-300 ${route.isExpanded ? "rotate-180" : ""
                  }`}
                width="20px"
              />
            </div>

            {route.isExpanded && (
              <div className="px-12 pb-4">
                <div className="relative pl-6">
                  {getRoutePoints(route)?.map((point, idx, points) => (
                    <div key={idx} className="mb-0 relative">
                      {/* Vertical line */}
                      {idx < points.length - 1 && (
                        <div className="absolute left-[0.2rem] top-3 h-full border-[1px] border-dashed border-[#444FBD] -ml-3"></div>
                      )}

                      {/* Point marker */}
                      <div className="absolute left-0 top-1 rounded-full bg-white border-1 border-[#444FBD] -ml-3 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-[#444FBD]"></div>
                      </div>

                      {/* Point text */}
                      <div className="ml-4">
                        <p className="font-[14px] font-[500] text-gray-900">{point?.name}</p>
                        <p className="text-sm text-gray-600">{point?.location}</p>
                        <p className="text-xs text-gray-500">{point?.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RouteSelection;